import React, {
  useState,
  useEffect
} from 'react';

import { v4 as uuidv4 } from 'uuid';

import { makeStyles } from '@material-ui/core/styles';
import {
  Button,
  FormControl,
  FormHelperText,
  InputLabel,
  Select,
  ListItemText,
  MenuItem,
  Checkbox,
  Avatar,
  Tooltip,
} from '@material-ui/core';

import {
  BaseAPI,
} from '../../../data/BaseAPI';

import {ProfileChip} from '../singles';
import localization from '../../../utils/localizations';
import {
  User
} from '../../../types';

const useStyles = makeStyles((theme) => ({
  listItemText: {
    marginLeft: 10,
  },
  horizontalListItem: {
    marginRight: 5,
  }
}));


function sortUsers(users: User[]): User[] {
  const strippedPrefixes = [
    /^(Prof\.\ Dr\.\-Ing\.\,\ Ir\.)/,
    /^(Prof\.\ Dr\.Ir\.)/,
    /^(Prof\.Dr\.)/,
    /^(Dr\.\-Ing\.)/,
    /^(Prof\.\ dr\.)/,
    /^(Dr\.\ Ir\.)/,
    /^(Prof\.\ Ir\.)/,
    /^(Dra\.)/,
    /^(Drs\.)/,
    /^(Ir\.)/,
    /^(\-Ing\.\.)/,
    /^(Dr.)/,
    /^(DPhil\ \(cand\))/,
    /^(Dr\.Ir\.)/,
    /^(Prof\.)/,
    /^(dr\.)/,
  ];
  const sortedUsers = users.sort((c1, c2) => {
    let name1 = c1.full_name;
    let name2 = c2.full_name;
    strippedPrefixes.forEach((prefix) => {
      name1 = name1.replace(prefix,"").trim();
      name2 = name2.replace(prefix,"").trim();
    });
    if (name1 > name2) { return 1; }
    if (name1 < name2) { return -1; }
    return 0;
  });

  return sortedUsers;
}


function UserSelectorField(props: {
  role: string;
  label: string;
  helpText?: string;
  error?: string;
  filter: any|undefined;
  multiple?: boolean;
  values?: string[]|string;
  excludes?: string[];
  showNone?: boolean;
  onChanged?: (values: unknown) => void;
}) {
  const classes = useStyles();

  const [inputId, setInputId] = useState(null as null|string);
  const [users, setUsers] = useState([] as User[]);

  const loadUsers = async () => {
    const api = new BaseAPI();
    try {
      const result = await api.get(`${props.role}/`);
      if (result) {
        setUsers(sortUsers(result as User[]));
      }
    } catch (error) {
      console.error(error);
    }
  }

  useEffect(() => {
    setInputId(uuidv4());
    loadUsers();
  }, []);

  if (!inputId) return <></>;

  return (
    <FormControl variant="outlined" fullWidth error={!!props.error}>
      <InputLabel id={`label_${inputId}`}>{props.label}</InputLabel>
      <Select
        labelId={`label_${inputId}`}
        id={inputId}
        value={props.values ? props.values : (props.multiple ? [] : '')}
        onChange={(event) => {
          if (event.target.value === '') {
            if (props.onChanged) return props.onChanged(undefined);
          }

          const selected = event.target.value as string[];
          if (props.onChanged) props.onChanged(selected);
        }}
        label={props.label}
        multiple={props.multiple}
        renderValue={(selected) => {
          if (typeof selected == 'string') {
            return <ProfileChip id={selected} role={props.role} className={classes.horizontalListItem} />
          }
          else {
            const selectedValues = selected as string[];
            return selectedValues.map((user) => <ProfileChip id={user} role={props.role} className={classes.horizontalListItem} />);
          }
        }}
      >
        {props.showNone &&
        <MenuItem key={`user-item-${props.role}-none`} value={""}>
          <ListItemText primary={<em>None</em>} />
        </MenuItem>
        }
        {users.map((user, i) => {
          return (
            <MenuItem
              key={`user-item-${props.role}-${user.id}`}
              value={user.id}
              disabled={((props.excludes && props.excludes.indexOf(user.id) > -1)) ? true : false}
            >
              {props.multiple &&
                <Checkbox checked={props.values && props.values.indexOf ? props.values.indexOf(user.id) > -1 : false} />
              }
              <Avatar src={user.avatar} />
              <Tooltip
                title={(user as any).bio ? (user as any).bio : ""}
                arrow
              >
                <ListItemText
                  className={classes.listItemText}
                  primary={user.full_name}
                  secondary={(user as any).advising_load ? `${localization.advisorLoadStatus}: ${(user as any).advising_load}` : ""}
                />
              </Tooltip>

              {(user as any).link &&
                <Button
                  onClick={(e) => {
                    e.stopPropagation();
                    window.open((user as any).link, '_blank');
                  }}
                >{localization.lecturerProfile}</Button>
              }
            </MenuItem>
          );
        })}
      </Select>
      {props.helpText && <FormHelperText>{props.helpText}</FormHelperText>}
      {props.error && <FormHelperText>{props.error}</FormHelperText>}
    </FormControl>
  );
}

export {UserSelectorField};